import { Building2, Check, Clock, Cpu, User, UserCircle, X } from "lucide-react";
import { useState, useEffect } from "react";
import BONavigation from "../../../components/admin/BONavigation";
import { getReviewedEnrichments, ReviewedEnrichment } from "../../../interfaces/events";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useDebouncedEffect } from "../../../hooks/useDebouncedEffect";

const ENRICHMENT_TYPE_LABELS: Record<string, string> = {
    linkedin: "LinkedIn",
    google_place: "Google Maps",
    google_place_DFS: "Google Maps (DFS)",
    website: "Website",
    siren: "SIREN",
    company_email: "Email entreprise",
    company_linked: "Entreprise liée",
    image: "Image",
    email: "Email",
    email_personal: "Email personnel",
};

export default function ReviewedEnrichments() {
    const [enrichments, setEnrichments] = useState<ReviewedEnrichment[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [statusFilter, setStatusFilter] = useState("");
    const [reviewTypeFilter, setReviewTypeFilter] = useState("");
    const [enrichmentTypeFilter, setEnrichmentTypeFilter] = useState("");
    const [entityTypeFilter, setEntityTypeFilter] = useState("");
    const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const fetchEnrichments = async () => {
        setLoading(true);
        try {
            const params: any = { page, limit: 50, sortOrder };
            if (statusFilter) params.status = statusFilter;
            if (reviewTypeFilter) params.isManuallyReviewed = reviewTypeFilter;
            if (enrichmentTypeFilter) params.enrichmentType = enrichmentTypeFilter;
            if (entityTypeFilter) params.entityType = entityTypeFilter;
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;

            const res = await getReviewedEnrichments(params);
            setEnrichments(res.data);
            setTotal(res.meta.total);
            setTotalPages(res.meta.totalPages);
        } catch (err) {
            console.error("Failed to fetch reviewed enrichments:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
    }, [statusFilter, reviewTypeFilter, enrichmentTypeFilter, entityTypeFilter, sortOrder, startDate, endDate]);

    useDebouncedEffect(() => {
        fetchEnrichments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, statusFilter, reviewTypeFilter, enrichmentTypeFilter, entityTypeFilter, sortOrder, startDate, endDate]);

    const resetFilters = () => {
        setStatusFilter("");
        setReviewTypeFilter("");
        setEnrichmentTypeFilter("");
        setEntityTypeFilter("");
        setSortOrder("desc");
        setStartDate("");
        setEndDate("");
    };

    const hasActiveFilters =
        statusFilter || reviewTypeFilter || enrichmentTypeFilter || entityTypeFilter || startDate || endDate || sortOrder !== "desc";

    const formatDate = (dateString: string) => {
        try {
            return format(new Date(dateString), "d MMM yyyy 'à' HH:mm", { locale: fr });
        } catch {
            return dateString;
        }
    };

    const getStatusBadge = (status: string) => {
        if (status === "approved") {
            return (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                    <Check className="h-3.5 w-3.5" />
                    Approuvé
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
                <X className="h-3.5 w-3.5" />
                Rejeté
            </span>
        );
    };

    const getReviewTypeBadge = (isManual: boolean) => {
        if (isManual) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                    <UserCircle className="h-3.5 w-3.5" />
                    Manuelle
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
                <Cpu className="h-3.5 w-3.5" />
                Automatique
            </span>
        );
    };

    const getReviewerLabel = (item: ReviewedEnrichment) => {
        if (!item.enrichment.isManuallyReviewed) return "Système";
        const user = item.enrichment.reviewedByUser;
        return user?.email || user?.name || item.enrichment.reviewedBy || "Admin";
    };

    const renderEnrichmentData = (enrichment: ReviewedEnrichment) => {
        const { type, data } = enrichment.enrichment;

        if (!data) return <span className="text-sm text-gray-400">Aucune donnée</span>;

        if (type === "linkedin" && typeof data === "object") {
            const url = data.url?.url || data.url || data.linkedin_url;
            return (
                <div className="text-sm">
                    {url && (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:underline">
                            {url}
                        </a>
                    )}
                </div>
            );
        }

        if (type === "google_place" && typeof data === "object") {
            return (
                <div className="text-sm space-y-1">
                    {data.display_name && <div className="font-medium">{data.display_name}</div>}
                    {data.formatted_address && <div className="text-gray-600">{data.formatted_address}</div>}
                </div>
            );
        }

        if (type === "website" && typeof data === "object") {
            const url = data.url || (data.domain ? `https://${data.domain}` : null);
            return (
                <div className="text-sm">
                    {url && (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:underline">
                            {url}
                        </a>
                    )}
                </div>
            );
        }

        if (type === "siren" && typeof data === "object") {
            return (
                <div className="text-sm space-y-1">
                    {data.siren && <div>SIREN: <span className="font-mono">{data.siren}</span></div>}
                    {data.company_name && <div className="font-medium">{data.company_name}</div>}
                </div>
            );
        }

        if (type === "company_email" && typeof data === "string") {
            return (
                <a href={`mailto:${data}`} className="text-sm text-gray-700 hover:underline">
                    {data}
                </a>
            );
        }

        try {
            const displayData = typeof data === "string" ? data : JSON.stringify(data, null, 2);
            return (
                <pre className="text-xs text-gray-700 bg-gray-50 p-2 rounded overflow-x-auto max-w-md">
                    {displayData}
                </pre>
            );
        } catch {
            return <span className="text-sm text-gray-400">Données invalides</span>;
        }
    };

    const extractOptionLabel = (option: any, type: string): string => {
        const getString = (val: any): string | null => {
            if (typeof val === "string") return val;
            if (val && typeof val === "object" && val.title && typeof val.title === "string") return val.title;
            return null;
        };

        if (type === "linkedin") {
            const linkedinData = option.url && typeof option.url === "object" ? option.url : option;
            if (linkedinData.title && typeof linkedinData.title === "string") {
                const parts = linkedinData.title.split(" - ");
                return parts[0].trim();
            }
            if (linkedinData.url && typeof linkedinData.url === "string") {
                const match = linkedinData.url.match(/linkedin\.com\/in\/([^/?]+)/);
                if (match) {
                    const slug = match[1];
                    return slug.split("-").map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(" ");
                }
            }
            return getString(option.name) || getString(option.fullName) ||
                (typeof option.url === "string" ? option.url : null) || "Profil LinkedIn";
        }
        if (type === "google_place") {
            return getString(option.display_name) || getString(option.name) ||
                getString(option.formatted_address) || "Lieu Google";
        }
        if (type === "website") {
            return getString(option.domain) || (typeof option.url === "string" ? option.url : null) || "Site web";
        }
        if (type === "siren") {
            return option.siren || getString(option.company_name) || "SIREN";
        }
        if (type === "company_linked") {
            return getString(option.companyName) || getString(option.name) || "Entreprise";
        }
        const safeName = getString(option.name) || getString(option.label);
        if (safeName) return safeName;
        try {
            return JSON.stringify(option).slice(0, 40);
        } catch {
            return "Option";
        }
    };

    const renderRejectedOptions = (enrichment: ReviewedEnrichment) => {
        const { type, options } = enrichment.enrichment;

        if (!options || options.length === 0) {
            return <div className="text-sm text-gray-500 italic">Aucune option alternative n'était disponible</div>;
        }

        return (
            <div className="space-y-2">
                <div className="text-sm font-medium text-gray-500 mb-2">Options rejetées ({options.length})</div>
                <div className="space-y-2">
                    {options.map((option: any, idx: number) => {
                        const label = extractOptionLabel(option, type);
                        const rawScore = option.score || option.relevanceScore || option.confidence || 0;
                        // LinkedIn scores are 0-100 integers; all other enrichment scores are 0-1 floats
                        const score = type === "linkedin" ? Math.round(rawScore) : Math.round(rawScore * 100);
                        return (
                            <div key={idx} className="bg-red-50 border border-red-200 rounded-md p-3 text-sm">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                        <div className="font-medium text-gray-900 mb-1">{label}</div>
                                        {type === "linkedin" && option.url?.url && (
                                            <a href={option.url.url} target="_blank" rel="noopener noreferrer"
                                                className="text-xs text-gray-700 hover:underline block truncate">
                                                {option.url.url}
                                            </a>
                                        )}
                                        {type === "google_place" && option.formatted_address && (
                                            <div className="text-xs text-gray-600">{option.formatted_address}</div>
                                        )}
                                        {type === "website" && option.domain && (
                                            <div className="text-xs text-gray-600">{option.domain}</div>
                                        )}
                                        {type === "siren" && option.siren && (
                                            <div className="text-xs font-mono text-gray-600">SIREN: {option.siren}</div>
                                        )}
                                    </div>
                                    {score > 0 && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 shrink-0">
                                            {score}%
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages: (number | "...")[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (page > 3) pages.push("...");
            for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
            if (page < totalPages - 2) pages.push("...");
            pages.push(totalPages);
        }

        return (
            <div className="mt-6 flex justify-center items-center gap-1">
                <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-[#F9F6F3] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Précédent
                </button>
                <div className="flex items-center gap-1 mx-1">
                    {pages.map((p, idx) =>
                        p === "..." ? (
                            <span key={`ellipsis-${idx}`} className="px-2 text-sm text-gray-400">…</span>
                        ) : (
                            <button
                                key={p}
                                onClick={() => setPage(p as number)}
                                className={`w-8 h-8 text-sm font-medium rounded-lg border transition-colors ${
                                    page === p
                                        ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                                        : "bg-white text-gray-700 border-gray-200 hover:bg-[#F9F6F3]"
                                }`}
                            >
                                {p}
                            </button>
                        )
                    )}
                </div>
                <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-[#F9F6F3] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Suivant
                </button>
            </div>
        );
    };

    const selectClass = "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 bg-white";

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <BONavigation />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Enrichissements revus</h1>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {loading ? "Chargement…" : `${total} enrichissement${total !== 1 ? "s" : ""} revu${total !== 1 ? "s" : ""}`}
                        </p>
                    </div>
                </div>

                {/* Filter bar */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 items-end">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Statut</label>
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectClass}>
                                <option value="">Tous</option>
                                <option value="approved">Approuvé</option>
                                <option value="rejected">Rejeté</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Revue</label>
                            <select value={reviewTypeFilter} onChange={(e) => setReviewTypeFilter(e.target.value)} className={selectClass}>
                                <option value="">Tous</option>
                                <option value="true">Manuelle</option>
                                <option value="false">Automatique</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Enrichissement</label>
                            <select value={enrichmentTypeFilter} onChange={(e) => setEnrichmentTypeFilter(e.target.value)} className={selectClass}>
                                <option value="">Tous</option>
                                <option value="linkedin">LinkedIn</option>
                                <option value="google_place">Google Maps</option>
                                <option value="google_place_DFS">Google Maps (DFS)</option>
                                <option value="website">Website</option>
                                <option value="siren">SIREN</option>
                                <option value="company_email">Email entreprise</option>
                                <option value="company_linked">Entreprise liée</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Entité</label>
                            <select value={entityTypeFilter} onChange={(e) => setEntityTypeFilter(e.target.value)} className={selectClass}>
                                <option value="">Toutes</option>
                                <option value="event">Événement</option>
                                <option value="person">Personne</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Du</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className={selectClass}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Au</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className={selectClass}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Tri</label>
                            <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")} className={selectClass}>
                                <option value="desc">Plus récent</option>
                                <option value="asc">Plus ancien</option>
                            </select>
                        </div>
                    </div>
                    {hasActiveFilters && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                            <button
                                onClick={resetFilters}
                                className="text-xs text-gray-500 hover:text-gray-900 underline"
                            >
                                Réinitialiser les filtres
                            </button>
                        </div>
                    )}
                </div>

                {/* Loading */}
                {loading && (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300"></div>
                    </div>
                )}

                {/* Empty state */}
                {!loading && enrichments.length === 0 && (
                    <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                        <p className="text-gray-500">Aucun enrichissement revu trouvé</p>
                    </div>
                )}

                {/* Enrichments list */}
                {!loading && enrichments.length > 0 && (
                    <div className="space-y-4">
                        {enrichments.map((item, idx) => (
                            <div
                                key={idx}
                                className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-sm transition-shadow"
                            >
                                {/* Header */}
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            {item.entityType === "event" ? (
                                                <Building2 className="h-5 w-5 text-gray-400" />
                                            ) : (
                                                <User className="h-5 w-5 text-gray-400" />
                                            )}
                                            <h3 className="text-base font-semibold text-gray-900">
                                                {item.companyName || item.personName || "Inconnu"}
                                            </h3>
                                        </div>
                                        {item.source && (
                                            <div className="text-sm text-gray-500">Source : {item.source}</div>
                                        )}
                                        {item.personRole && (
                                            <div className="text-sm text-gray-500">Rôle : {item.personRole}</div>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end gap-2 shrink-0">
                                        {getStatusBadge(item.enrichment.status)}
                                        {getReviewTypeBadge(item.enrichment.isManuallyReviewed)}
                                        <div className="text-xs text-gray-500">
                                            Revu par :{" "}
                                            <span className="text-gray-900">{getReviewerLabel(item)}</span>
                                        </div>
                                        <button
                                            disabled={!item.establishmentId}
                                            onClick={() => {
                                                if (!item.establishmentId) return;
                                                window.open(`/establishments/${item.establishmentId}`, "_blank");
                                            }}
                                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-[#F9F6F3] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Ouvrir l'établissement
                                        </button>
                                    </div>
                                </div>

                                {/* Enrichment details */}
                                <div className="border-t border-gray-100 pt-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <div className="text-xs font-medium text-gray-500 mb-1">Type d'enrichissement</div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {ENRICHMENT_TYPE_LABELS[item.enrichment.type] || item.enrichment.type}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                                                <Clock className="h-3.5 w-3.5" />
                                                Date de revue
                                            </div>
                                            <div className="text-sm text-gray-900">
                                                {formatDate(item.enrichment.reviewedAt)}
                                                {item.enrichment.reviewedAtRecoveredFrom && (
                                                    <span className="ml-1 text-gray-400" title={`Date reconstituée (${item.enrichment.reviewedAtRecoveredFrom})`}>≈</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {item.enrichment.status === "approved" && (
                                        <div className="mt-4">
                                            <div className="text-xs font-medium text-gray-500 mb-2">
                                                Données approuvées
                                                {item.dataFromEstablishment && (
                                                    <span className="ml-2 font-normal text-gray-400" title="La valeur approuvée n'a pas été conservée : voici ce que la fiche contient aujourd'hui.">
                                                        · valeurs actuelles de la fiche établissement
                                                    </span>
                                                )}
                                            </div>
                                            <div className="bg-gray-50 rounded-lg p-3">
                                                {renderEnrichmentData(item)}
                                            </div>
                                        </div>
                                    )}

                                    {item.enrichment.status === "rejected" && (
                                        <div className="mt-4">{renderRejectedOptions(item)}</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {renderPagination()}
            </div>
        </div>
    );
}
